import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import LocationService from '@/domain/Location/LocationService';
import NonRevenueWaterService from '@/domain/NonRevenueWater/NonRevenueWaterService';
import useInfrastructure from '@/hooks/useInfrastructure';
import useLocation from '@/hooks/useLocation';
import useParameter from '@/hooks/useParameter';
import { getUnitsAction } from '@/redux/slices/unitsSlice';
import { DeleteOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import {
	Button as BaseButton,
	DataGrid,
	DatePicker,
	Input,
	Tooltip,
} from '@hydrodigital/design-system';
import { notification } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

// Temporary type extension until @hydrodigital/design-system updates ButtonProps to include 'type'
const Button = BaseButton as React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<typeof BaseButton> & {
		type?: 'button' | 'submit' | 'reset';
	} & React.RefAttributes<HTMLButtonElement>
>;

const locationService = new LocationService();

// Zod Schemas
const configSchema = z.object({
	locationId: z
		.union([z.number(), z.string(), z.null()])
		.refine((val) => val !== null && val !== undefined, {
			message: 'Please select a location',
		}),
	infrastructure: z.any().refine((val) => val !== null && val !== undefined, {
		message: 'Please select an asset',
	}),
	parameterId: z.any().refine((val) => val !== null && val !== undefined, {
		message: 'Please select a parameter',
	}),
	measurementTypeId: z
		.union([z.number(), z.null()])
		.refine((val) => val !== null && val !== undefined, {
			message: 'Please select a unit measurement',
		}),
});

type ConfigFormData = {
	locationId: string | number | null;
	infrastructure: any;
	parameterId: any;
	measurementTypeId: number | null;
};

const entrySchema = z.object({
	dateRange: z
		.object({
			from: z.date().optional(),
			to: z.date().optional(),
		})
		.refine((range) => range.from && range.to, { message: 'Please select a date range' }),
	value: z
		.union([z.number(), z.string(), z.literal('')])
		.refine((val) => val !== null && val !== undefined && val !== '', {
			message: 'Enter an invoice value',
		}),
});

type EntryFormData = {
	dateRange: { from?: Date; to?: Date };
	value: string | number;
};
const NonRevenueWaterView: React.FC = () => {
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [entries, setEntries] = useState<any[]>([]);
	const [nrwId, setNrwId] = useState<number | null>(null);
	const [nrwInvoiceId, setnrwInvoiceId] = useState<number | null>(null);
	const units = useSelector((state: any) => state.units.array);
	const userId = localStorage.getItem('userId') || '';
	const [isVisible, setIsVisible] = useState(false);
	const [isVisible2, setIsVisible2] = useState(false);
	const [isVisible3, setIsVisible3] = useState(false);
	const [newRule, setNewRule] = useState<any>({
		selectedInfrastructure: null,
		selectedParam: null,
		criticalLow: null,
		critialHigh: null,
	});
	const [searchParams, setSearchParams] = useSearchParams();
	const [location, setLocation] = useState<any>(null);
	const [infrastructureName, setInfrastructureName] = useState<string | null>(null);

	// React Hook Form
	const formConfig = useForm<ConfigFormData>({
		resolver: zodResolver(configSchema),
		mode: 'onChange',
		defaultValues: {
			locationId: null,
			infrastructure: null,
			parameterId: null,
			measurementTypeId: null,
		},
	});

	const formEntry = useForm<EntryFormData>({
		resolver: zodResolver(entrySchema),
		mode: 'onChange',
		defaultValues: {
			dateRange: { from: undefined, to: undefined },
			value: '',
		},
	});

	const dispatch: any = useDispatch();
	const nonRevenueWaterService = new NonRevenueWaterService();
	const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
	const isUpdatingFromCombobox = useRef(false);

	// Watch form entry fields for validation
	const watchedEntryFields = useWatch({
		control: formEntry.control,
		name: ['dateRange', 'value'],
	});

	// Hooks for data fetching
	const { locations } = useLocation({ mode: 'ByUser', userId });

	const { infrastructures, setParams: setInfraParams } = useInfrastructure({
		name: infrastructureName,
		mode: 'ByLocation',
		location,
		organizationId: null,
		size: 50,
	});

	const { parameters } = useParameter({
		infrastructureId: newRule.selectedInfrastructure?.value || null,
	});


	// Parse locations to Combobox format (hierarchical)
	const parseLocations = (locations: any[]): any[] => {
		return locations.map((location) => ({
			value: location.locationId,
			label: location.locationName,
			children: location.childLocations ? parseLocations(location.childLocations) : [],
			disabled: !location.canShowLocation,
		}));
	};

	// Helper: Buscar recursivamente una location por ID en estructura jerárquica
	const findLocationById = (locations: any[], locationId: number): any => {
		for (const location of locations) {
			if (location.locationId === locationId) {
				return location;
			}
			if (location.childLocations && location.childLocations.length > 0) {
				const found = findLocationById(location.childLocations, locationId);
				if (found) return found;
			}
		}
		return null;
	};

	// Helper: Aplanar locations jerárquicas para Combobox (sin children)
	const flattenLocations = (locations: any[], prefix = ''): any[] => {
		const result: any[] = [];
		locations.forEach((location) => {
			const label = prefix ? `${prefix} > ${location.locationName}` : location.locationName;
			result.push({
				value: String(location.locationId), // ✅ Convertir a string para Combobox
				label: label,
				disabled: !location.canShowLocation,
			});
			if (location.childLocations && location.childLocations.length > 0) {
				result.push(...flattenLocations(location.childLocations, label));
			}
		});
		return result;
	};

	useEffect(() => {
		if (isUpdatingFromCombobox.current) {
			isUpdatingFromCombobox.current = false;
			return;
		}

		const locationId = searchParams.get('locationId');
		if (locationId) {
			const numLocationId = Number(locationId);
			formConfig.setValue('locationId', numLocationId);
			handleGetNonRevenueWater(numLocationId);
		} else {
			clearFormAndState();
		}
	}, [searchParams]);

	useEffect(() => {
		dispatch(getUnitsAction());
	}, [dispatch]);

	const clearFormAndState = (preserveLocationId?: number | null) => {
		formConfig.reset({
			measurementTypeId: null,
			infrastructure: null,
			parameterId: null,
			locationId: preserveLocationId ?? null,
		});

		setInfrastructureName(null);
		setEntries([]);
		setNewRule({
			selectedInfrastructure: null,
			selectedParam: null,
			unit: null,
		});
		setNrwId(null);
	};

	const handleGetNonRevenueWater = async (locationId: number | null) => {
		clearFormAndState(locationId);

		if (!locationId) return;

		try {
			const response = await nonRevenueWaterService.getConfiguration(locationId);

			if (response.nrwId === null) {
				setNrwId(null);
				setIsVisible(true);
				setIsVisible2(false);
				setIsVisible3(true);
			} else {
				const formattedInvoices = response.invoice.map((entry: any) => ({
					...entry,
					startDate: dayjs(entry.startDate).format('YYYY-MM-DD'),
					endDate: dayjs(entry.endDate).format('YYYY-MM-DD'),
				}));

				setNrwId(response.nrwId);
				setIsVisible(true);
				setIsVisible2(true);
				setIsVisible3(false);

				const infrastructureValue = { value: response.infrastructure };
				const parameterValue = { value: String(response.parameterId) };

				formConfig.reset({
					locationId: response.locationId,
					measurementTypeId: response.measurementTypeId,
					infrastructure: infrastructureValue,
					parameterId: parameterValue,
				});

				setNewRule((prev: any) => ({
					...prev,
					selectedInfrastructure: infrastructureValue,
					selectedParam: parameterValue,
					unit: response.measurementTypeId,
				}));

				setEntries(formattedInvoices);

				setInfraParams({
					name: '',
					location: response.locationId,
					infraId: null,
					infraTypeId: null,
					organizationId: null,
				});
			}
		} catch (error) {
			console.error('Error fetching Non-Revenue Water data:', error);
		}
	};

	const handleViewLocations = async () => {
		if (!newRule.unit) {
			notification.error({
				message: 'Error',
				description: 'Please select a Unit Measurement',
			});
			return;
		}

		try {
			const response = await nonRevenueWaterService.createOrUpdateConfiguration({
				nrwId: nrwId ?? null,
				locationId: location ?? 0,
				infrastructure: newRule.selectedInfrastructure?.value,
				parameterId: Number(newRule.selectedParam?.value),
				measurementTypeId: newRule.unit,
			});

			if (response.success !== 0) {
				notification.success({
					message: 'Successful',
					description: 'Configuration saved successfully',
				});
			} else {
				notification.error({
					message: 'Error',
					description: 'Error saving configuration',
				});
			}
			await handleGetNonRevenueWater(location);
		} catch (error) {
			console.error('Error saving configuration:', error);
			notification.error({
				message: 'Error',
				description: 'An error occurred while saving the configuration',
			});
		}
	};

	const handleAddEntry = async () => {
		try {
			const values = formEntry.getValues();
			const startDate = values.dateRange?.from;
			const endDate = values.dateRange?.to;

			const formattedStartDateForService = startDate
				? dayjs(startDate).format('YYYY-MM-DD HH:mm:ss')
				: null;
			const formattedEndDateForService = endDate
				? dayjs(endDate).format('YYYY-MM-DD HH:mm:ss')
				: null;

			const formattedStartDateForEntry = startDate ? dayjs(startDate).format('YYYY-MM-DD') : null;
			const formattedEndDateForEntry = endDate ? dayjs(endDate).format('YYYY-MM-DD') : null;

			const defaultNwrId = nrwId ?? 0;

			if (!formattedStartDateForService || !formattedEndDateForService) {
				notification.error({
					message: 'Error',
					description: 'Please select a valid date range',
				});
				return;
			}

			const response = await nonRevenueWaterService.addInvoiceToConfiguration({
				nrwId: defaultNwrId,
				startDate: formattedStartDateForService,
				endDate: formattedEndDateForService,
				invoice: typeof values.value === 'string' ? parseFloat(values.value) : values.value,
			});

			if (response.nrwInvoiceId) {
				const newEntry = {
					nrwInvoiceId: response.nrwInvoiceId,
					startDate: formattedStartDateForEntry,
					endDate: formattedEndDateForEntry,
					value: values.value,
				};

				setEntries([...entries, newEntry]);
				formEntry.reset();
				setIsAddButtonDisabled(true);

				notification.success({
					message: 'Successful',
					description: 'Invoice added successfully',
				});
			} else {
				notification.error({
					message: 'Error',
					description: 'Error adding the invoice',
				});
				console.error('Failed to add entry:', response.message || 'Unknown error');
			}
		} catch (error) {
			console.error('Error adding the invoice:', error);
		}
	};

	// Dynamic validation for add button
	useEffect(() => {
		const [dateRange, value] = watchedEntryFields;
		const hasValidRange = dateRange?.from && dateRange?.to;
		setIsAddButtonDisabled(!hasValidRange || value === undefined || value === null || value === '');
	}, [watchedEntryFields]);

	const handleDeleteEntry = async () => {
		if (!nrwInvoiceId) return;

		try {
			const response = await nonRevenueWaterService.deleteInvoiceFromConfiguration(nrwInvoiceId);

			if (response.success) {
				setEntries(entries.filter((entry) => entry.nrwInvoiceId !== nrwInvoiceId));
				setShowConfirmDelete(false);
				notification.success({
					message: 'Successful',
					description: 'Inovice deleted Successfully',
				});
			} else {
				notification.error({
					message: 'Error',
					description: 'Failed to delete invoice',
				});
				console.error('Failed to delete invoice:', response.message);
			}
		} catch (error) {
			console.error('Error deleting invoice:', error);
		}
	};

	const handleChange = (value: number | null) => {
		setNewRule((prev: any) => ({
			...prev,
			unit: value,
		}));
	};

	const handleGenerateInvoice = async (invoiceValue: number) => {
		try {
			const response = await nonRevenueWaterService.calculateInvoice({
				invoiceValue,
			});
			notification.success({
				message: 'Successful',
				description: 'Inovice calculated Successfully',
			});
		} catch (error) {
			notification.error({
				message: 'Error',
				description: 'Error calculating invoice',
			});
			console.error('Error generating invoice:', error);
		}
	};

	const columnsTable: any[] = [
		{
			id: 'dateRange',
			accessorKey: 'startDate',
			header: 'Date Range',
			cell: ({ row }) => (
				<div>
					<span>{`${row.original.startDate} - ${row.original.endDate}`}</span>
				</div>
			),
		},
		{
			id: 'value',
			accessorKey: 'value',
			header: 'Volume of water billed',
		},
		{
			id: 'action',
			header: 'Action',
			cell: ({ row }) => (
				<div>
					<Tooltip title="Delete">
						<DeleteOutlined
							style={{ fontSize: '1.5rem', cursor: 'pointer' }}
							onClick={() => {
								setShowConfirmDelete(true);
								setnrwInvoiceId(row.original.nrwInvoiceId);
							}}
						/>
					</Tooltip>
					<a
						style={{ marginLeft: 16, cursor: 'pointer', color: '#1890ff' }}
						onClick={() => handleGenerateInvoice(row.original.nrwInvoiceId)}
					>
						Generate
					</a>
				</div>
			),
		},
	];

	const dataSource = entries;

	return (
		<div
			className="flex column align-items-star"
			style={{ overflow: 'auto', width: '100%', height: '100vh' }}
		>
			<div className="text-title-page">Non Revenue Water</div>
			<div className="flex column pl-3 pr-3">
				<form onSubmit={formConfig.handleSubmit(handleViewLocations)}>
					<div className="flex row align-start" style={{ marginBottom: '10px' }}>
						{!isVisible && (
							<label style={{ width: '100%', textAlign: 'left', paddingRight: '10px' }}>
								Select a location to get information about invoice configuration
							</label>
						)}
					</div>
					<div className="flex row align-start" style={{ marginBottom: '10px' }}>
						<label style={{ width: '20%', textAlign: 'left', paddingRight: '10px' }}>
							Location
						</label>
						<Controller
							name="locationId"
							control={formConfig.control}
							render={({ field, fieldState }) => {
								const comboboxValue =
									field.value !== null && field.value !== undefined
										? String(field.value)
										: undefined;
								const flatOptions = flattenLocations(locations);

								return (
									<div style={{ width: '30%', margin: '0' }}>
										<Combobox
											items={flatOptions}
											value={comboboxValue}
											onValueChange={(value: string) => {
												const numValue = value ? Number(value) : null;
												isUpdatingFromCombobox.current = true;

												field.onChange(numValue);
												setLocation(numValue);
												handleGetNonRevenueWater(numValue);
												if (numValue) {
													setSearchParams({ locationId: numValue.toString() });
												} else {
													setSearchParams({});
												}
											}}
											onClear={() => {
												field.onChange(null);
												setLocation(null);
												setSearchParams({});
											}}
											size="small"
										>
											<ComboboxInput placeholder="Select Location" />
											<ComboboxContent>
												<ComboboxEmpty>No locations found.</ComboboxEmpty>
												<ComboboxList>
													{(item) => (
														<ComboboxItem key={item.value} value={item}>
															{item.label}
														</ComboboxItem>
													)}
												</ComboboxList>
											</ComboboxContent>
										</Combobox>
										{fieldState.error && (
											<span className="text-red-500 text-xs">{fieldState.error.message}</span>
										)}
									</div>
								);
							}}
						/>
					</div>
					{isVisible && (
						<div>
							<div className="flex row align-start" style={{ marginBottom: '10px' }}>
								<label
									style={{
										width: '20%',
										textAlign: 'left',
										paddingRight: '10px',
									}}
								>
									Measurement ID
								</label>
								<Controller
									name="infrastructure"
									control={formConfig.control}
									render={({ field, fieldState }) => (
										<div style={{ width: '30%', margin: '0' }}>
											<Combobox
												items={(infrastructures || []).map((i) => ({
													value: String(i.id),
													label: i.name,
												}))}
												value={field.value?.value?.toString()}
												onValueChange={(val: string) => {
													const selectedValue = { value: Number(val) };
													field.onChange(selectedValue);
													setNewRule((rl: any) => ({
														...rl,
														selectedInfrastructure: selectedValue,
														selectedParam: null,
														unit: null,
													}));
													formConfig.setValue('parameterId', null);
													formConfig.setValue('measurementTypeId', null);
												}}
												onSearchChange={(newValue: string) => {
													setInfrastructureName(newValue);
												}}
												onClear={() => {
													field.onChange(null);
													setNewRule((rl: any) => ({
														...rl,
														selectedInfrastructure: null,
														selectedParam: null,
														unit: null,
													}));
													setInfrastructureName(null);
												}}
												size="small"
											>
												<ComboboxInput placeholder="Select Asset" />
												<ComboboxContent>
													<ComboboxEmpty>No assets found.</ComboboxEmpty>
													<ComboboxList>
														{(item) => (
															<ComboboxItem key={item.value} value={item}>
																{item.label}
															</ComboboxItem>
														)}
													</ComboboxList>
												</ComboboxContent>
											</Combobox>
											{fieldState.error && (
												<span className="text-red-500 text-xs">{fieldState.error.message}</span>
											)}
										</div>
									)}
								/>
							</div>
							<div className="flex row align-start" style={{ marginBottom: '10px' }}>
								<label
									style={{
										width: '20%',
										textAlign: 'left',
										paddingRight: '10px',
									}}
								></label>
								<Controller
									name="parameterId"
									control={formConfig.control}
									render={({ field, fieldState }) => (
										<div style={{ width: '30%', margin: '0' }}>
											<Combobox
												items={(parameters || []).map((p) => ({
													value: p.id.toString(),
													label: p.name,
												}))}
												value={field.value?.value?.toString()}
												onValueChange={(val: string) => {
													const selectedParam = { value: val };
													field.onChange(selectedParam);
													setNewRule((rl: any) => ({
														...rl,
														selectedParam: selectedParam,
													}));
												}}
												onClear={() => {
													field.onChange(null);
													setNewRule((rl: any) => ({
														...rl,
														selectedParam: null,
													}));
												}}
												size="small"
											>
												<ComboboxInput placeholder="Select Parameter" />
												<ComboboxContent>
													<ComboboxEmpty>No parameters found.</ComboboxEmpty>
													<ComboboxList>
														{(item) => (
															<ComboboxItem key={item.value} value={item}>
																{item.label}
															</ComboboxItem>
														)}
													</ComboboxList>
												</ComboboxContent>
											</Combobox>
											{fieldState.error && (
												<span className="text-red-500 text-xs">{fieldState.error.message}</span>
											)}
										</div>
									)}
								/>
							</div>
							<div className="flex row align-start" style={{ marginBottom: '10px' }}>
								<label
									style={{
										width: '20%',
										textAlign: 'left',
										paddingRight: '10px',
									}}
								>
									Unit Measurement
								</label>
								<Controller
									name="measurementTypeId"
									control={formConfig.control}
									render={({ field, fieldState }) => (
										<div style={{ width: '30%', margin: '0' }}>
											<Combobox
												items={units.map((item: any) => ({
													value: item.id.toString(),
													label: item.name,
												}))}
												value={field.value?.toString()}
												onValueChange={(value: string) => {
													const numValue = value ? Number(value) : null;
													field.onChange(numValue);
													handleChange(numValue);
												}}
												size="small"
											>
												<ComboboxInput placeholder="Select Unit" />
												<ComboboxContent>
													<ComboboxEmpty>No units found.</ComboboxEmpty>
													<ComboboxList>
														{(item) => (
															<ComboboxItem key={item.value} value={item}>
																{item.label}
															</ComboboxItem>
														)}
													</ComboboxList>
												</ComboboxContent>
											</Combobox>
											{fieldState.error && (
												<span className="text-red-500 text-xs">{fieldState.error.message}</span>
											)}
										</div>
									)}
								/>
							</div>
						</div>
					)}
					{isVisible3 && (
						<div
							className="flex row justify-content-center"
							style={{ marginTop: '20px', width: '70%' }}
						>
							<Button
								type="submit"
								variant="primary"
								style={{ width: '14%', margin: 'auto' }}
								label="Save"
								showIcon={false}
							/>
						</div>
					)}
				</form>
			</div>
			{isVisible2 && (
				<div className="flex column pl-3 pr-3 mt-4">
					<form onSubmit={formEntry.handleSubmit(handleAddEntry)}>
						<div className="flex align-center" style={{ marginBottom: '20px' }}>
							<Controller
								name="dateRange"
								control={formEntry.control}
								render={({ field, fieldState }) => (
									<div style={{ width: '40%', margin: '0 25px 0 0' }}>
										<label
											style={{
												width: '100%',
												textAlign: 'left',
												paddingRight: '10px',
											}}
										>
											Date Range
										</label>
										<DatePicker
											mode="range"
											value={field.value as any}
											onChange={(range: any) => field.onChange(range)}
										/>
										{fieldState.error && (
											<span className="text-red-500 text-xs">{fieldState.error.message}</span>
										)}
									</div>
								)}
							/>
							<Controller
								name="value"
								control={formEntry.control}
								render={({ field, fieldState }) => (
									<div style={{ width: '25%', margin: '0' }}>
										<label className="text-xs">Volume of water billed</label>
										<Input
											placeholder="Invoice"
											style={{ width: '100%' }}
											size='small'
											value={field.value}
											onChange={(e) => field.onChange(e.target.value)}
										/>
										{fieldState.error && (
											<span className="text-red-500 text-xs">{fieldState.error.message}</span>
										)}
									</div>
								)}
							/>
							<Button
								type="submit"
								style={{ width: '10%', alignSelf: 'end', marginLeft: '10px' }}
								iconName="plus-circle"
								showLabel={false}
								disabled={isAddButtonDisabled}
							/>
						</div>
					</form>

					<DataGrid
						style={{ maxHeight: '650px', overflowX: 'auto', width: '70%' }}
						columns={columnsTable}
						data={dataSource}
						pagination={{ enabled: false }}
					/>
				</div>
			)}
			{isVisible2 && (
				<div className="flex row justify-content-center" style={{ margin: '20px 0', width: '70%' }}>
					<Button
						variant="primary"
						style={{ width: '14%' }}
						onClick={formConfig.handleSubmit(handleViewLocations)}
						label="Save"
						showIcon={false}
					/>

					<ConfirmationModal
						title="Delete Invoice?"
						message="Are you sure you want to delete this Invoice?"
						showModal={showConfirmDelete}
						onAccept={handleDeleteEntry}
						oncancel={() => {
							setShowConfirmDelete(false);
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default NonRevenueWaterView;
